import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import camera from '../assets/camera.png';
import router from '../assets/router.png';
import security from '../assets/security.png';
import sts from '../assets/sts.png';
import drone from '../assets/drone.png';
import anti from '../assets/anti.png';
import { Link } from 'react-router-dom';
export default function HeroSection({ onContactClick }) {
  const { t } = useTranslation('hero');

  const titlesInGenitive = t("titles", { returnObjects: true });

  function TypingEffect({ words, speed = 80, pause = 1800 }) {
    const [index, setIndex] = useState(0);
    const [subIndex, setSubIndex] = useState(0);
    const [deleting, setDeleting] = useState(false);
    const [blink, setBlink] = useState(true);
    const [displayedText, setDisplayedText] = useState("");

    useEffect(() => {
      const blinkInterval = setInterval(() => {
        setBlink((prev) => !prev);
      }, 500);
      return () => clearInterval(blinkInterval);
    }, []);

    useEffect(() => {
      const currentWord = words[index];

      if (!deleting && subIndex < currentWord.length + 1) {
        setDisplayedText(currentWord.substring(0, subIndex));
        const timeout = setTimeout(() => setSubIndex((prev) => prev + 1), speed);
        return () => clearTimeout(timeout);
      }

      if (!deleting && subIndex === currentWord.length + 1) {
        const timeout = setTimeout(() => setDeleting(true), pause);
        return () => clearTimeout(timeout);
      }

      if (deleting && subIndex > 0) {
        setDisplayedText(currentWord.substring(0, subIndex));
        const timeout = setTimeout(() => setSubIndex((prev) => prev - 1), speed / 2);
        return () => clearTimeout(timeout);
      }

      if (deleting && subIndex === 0) {
        setDeleting(false);
        setIndex((prev) => (prev + 1) % words.length);
      }
    }, [subIndex, deleting, index, words, speed, pause]);

    return (
      <span className="text-cyan-300">
        {displayedText}
        <span className={`inline-block w-[1ch] ${blink ? "opacity-100" : "opacity-0"} absolute`}>|</span>
      </span>
    );
  }

  const features = [
    { icon: camera, key: 'camera' },
    { icon: router, key: 'router' },
    { icon: sts, key: 'sts' },
    { icon: security, key: 'security' },
    { icon: drone, key: 'drone' },
    { icon: anti, key: 'anti' },
  ].map(({ icon, key }) => ({
    icon: <img src={icon} alt={t(`features.${key}.title`)} className="w-7 h-7" />,
    title: t(`features.${key}.title`),
    text: t(`features.${key}.text`),
  }));

  const handleSpotlight = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    e.currentTarget.style.setProperty('--y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <section className="relative text-white overflow-hidden">
      <div className="grid-bg opacity-60" />

      {/* Слева текст, справа компетенции */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[150px] pb-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-start relative z-10">
        <div>
          <h1
            className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6 animate-fade-in"
            style={{ animationFillMode: 'backwards' }}
          >
            {t("headline_prefix")} <br />
            {t("headline_suffix")}{" "}
            <span className="block min-h-[6rem]">
              <TypingEffect words={titlesInGenitive} />
            </span>
          </h1>

          <p
            className="text-base text-gray-300 max-w-md mb-8 animate-fade-in"
            style={{ animationDelay: '150ms', animationFillMode: 'backwards' }}
          >
            {t("description")}
          </p>

          <div
            className="flex gap-4 flex-wrap animate-fade-in"
            style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}
          >
            <button
              onClick={onContactClick}
              className="font-display btn-primary"
            >
              {t('company.contact_button')}
            </button>
            <Link
              to="/about"
              className="font-display btn-ghost"
            >
              {t("more")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((item, index) => (
            <div
              key={index}
              onMouseMove={handleSpotlight}
              className="spotlight glass-card p-5 rounded-2xl text-center flex flex-col items-center justify-center animate-fade-in"
              style={{ animationDelay: `${200 + index * 90}ms`, animationFillMode: 'backwards' }}
            >
              <div className="bracket tl" />
              <div className="bracket br" />
              <div className="font-mono text-[10px] tracking-wider text-cyan-300/70 absolute top-3 left-4">
                {String(index + 1).padStart(2, '0')}
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center mb-3">
                {item.icon}
              </div>
              <h3 className="font-display font-semibold leading-tight">{item.title}</h3>
              <p className="text-sm text-blue-100/70 mt-1">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
